/**
 * AIAssistBar · iter144
 *
 * Inline AI-Assistent für Textfelder. Zeigt sich als kompakte Button-Leiste
 * unterhalb des Feldes, greift auf `/api/ai-assist/text` zu und ersetzt/appendet
 * das Ergebnis in den bestehenden State via `onApply`.
 *
 * Props:
 *   area:        "MARKETPLACE" | "STX" | "SOCIAL"
 *   value:       aktueller Feld-Text (String oder HTML)
 *   onApply:     (newValue) => void
 *   fieldType:   "title" | "description" | "caption" | "announcement"
 *   maxChars:    Optionaler harter Cap
 *   tone:        Optionaler Ton-Hinweis
 *   testIdSuffix: für data-testid Unique-Suffix
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Wand2, Scissors, Maximize2, RefreshCw, SpellCheck, Loader2, ArrowUpRight, Smile, Hash } from "lucide-react";
import api from "@/lib/api";
import AIReviewDialog from "@/components/ai/AIReviewDialog";

const ACTIONS = [
  { key: "generate", label: "Generieren", icon: Sparkles },
  { key: "improve", label: "Verbessern", icon: Wand2 },
  { key: "shorten", label: "Kürzen", icon: Scissors },
  { key: "expand", label: "Erweitern", icon: Maximize2 },
  { key: "rephrase", label: "Umformulieren", icon: RefreshCw },
  { key: "spellcheck", label: "Rechtschr.", icon: SpellCheck },
];

// Extract plaintext from HTML (Rich-Text-Editor gibt HTML zurück)
function _plain(v) {
  if (!v) return "";
  return String(v).replace(/<[^>]+>/g, "").trim();
}

export default function AIAssistBar({ area, value, onApply, fieldType = "description", maxChars, tone, testIdSuffix = "" }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [upgradeHint, setUpgradeHint] = useState(null);
  // iter171 · EU AI Act · Review-Dialog vor Übernahme
  const [review, setReview] = useState(null); // {text, action}
  // iter169.6 · Emoji-Toggle · pro Feld persistiert in localStorage
  const emojiKey = `aiAssistEmoji:${area || "?"}:${fieldType}:${testIdSuffix || "default"}`;
  const [emojiOn, setEmojiOn] = useState(false);
  // iter169.7 · Hashtag-Toggle · pro Feld persistiert
  const hashtagKey = `aiAssistHashtags:${area || "?"}:${fieldType}:${testIdSuffix || "default"}`;
  const [hashtagsOn, setHashtagsOn] = useState(false);
  useEffect(() => {
    try {
      const e = localStorage.getItem(emojiKey);
      if (e !== null) setEmojiOn(e === "1");
      const h = localStorage.getItem(hashtagKey);
      if (h !== null) setHashtagsOn(h === "1");
    } catch { /* silent */ }
  }, [emojiKey, hashtagKey]);
  const toggleEmoji = () => {
    setEmojiOn((prev) => {
      const next = !prev;
      try { localStorage.setItem(emojiKey, next ? "1" : "0"); } catch { /* silent */ }
      return next;
    });
  };
  const toggleHashtags = () => {
    setHashtagsOn((prev) => {
      const next = !prev;
      try { localStorage.setItem(hashtagKey, next ? "1" : "0"); } catch { /* silent */ }
      return next;
    });
  };

  const run = async (action) => {
    setBusy(action); setError(null); setUpgradeHint(null);
    try {
      const plain = _plain(value);
      const payload = {
        area, action, context: plain || "(leer)",
        field_type: fieldType, tone: tone || undefined, max_chars: maxChars,
        emoji: emojiOn,
        hashtags: hashtagsOn,
      };
      const { data } = await api.post("/ai-assist/text", payload);
      // iter171 · EU-AI-Act Art. 50: Review-Dialog statt sofortiger Übernahme
      const generated = data.text || "";
      if (generated) {
        const actionLabel = (ACTIONS.find((a) => a.key === action) || {}).label || action;
        setReview({ text: generated, action: actionLabel });
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "FEATURE_NOT_INCLUDED" || d?.code === "QUOTA_EXCEEDED") {
        setUpgradeHint({
          code: d.code, plan: d.current_plan,
          url: d.upgrade_hint?.url || "/studio/creator-helper",
          cta: d.upgrade_hint?.cta || "Tarif ansehen und freischalten",
          used: d.used, limit: d.limit,
        });
      } else {
        setError(typeof d === "string" ? d : "AI-Fehler");
      }
    } finally { setBusy(null); }
  };

  return (
    <div data-testid={`ai-assist-bar${testIdSuffix ? "-" + testIdSuffix : ""}`} className="mt-1.5 space-y-1.5">
      <div className="flex flex-wrap items-center gap-1">
        {ACTIONS.map((a) => (
          <button key={a.key}
            data-testid={`ai-assist-${a.key}${testIdSuffix ? "-" + testIdSuffix : ""}`}
            onClick={() => run(a.key)} disabled={!!busy}
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[10px] font-semibold bg-[#F5F5F5] text-[#0A0A0A] hover:bg-[#E5E5E5] disabled:opacity-40 transition-colors">
            {busy === a.key ? <Loader2 size={10} className="animate-spin" /> : <a.icon size={10} />}
            {a.label}
          </button>
        ))}
        {/* iter169.6 · Emoji-Toggle */}
        <button
          type="button"
          data-testid={`ai-assist-emoji-toggle${testIdSuffix ? "-" + testIdSuffix : ""}`}
          aria-pressed={emojiOn}
          onClick={toggleEmoji}
          title={emojiOn ? "Emojis: an — klicken um zu deaktivieren" : "Emojis: aus — klicken um zu aktivieren"}
          className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[10px] font-semibold transition-colors border ${
            emojiOn
              ? "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200"
              : "bg-white text-[#666] border-[#E5E5E5] hover:bg-[#F5F5F5]"
          }`}
        >
          <Smile size={10} />
          Emojis {emojiOn ? "an" : "aus"}
        </button>
        {/* iter169.7 · Hashtag-Toggle */}
        <button
          type="button"
          data-testid={`ai-assist-hashtags-toggle${testIdSuffix ? "-" + testIdSuffix : ""}`}
          aria-pressed={hashtagsOn}
          onClick={toggleHashtags}
          title={hashtagsOn ? "Hashtags: an — klicken um zu deaktivieren" : "Hashtags: aus — klicken um zu aktivieren"}
          className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[10px] font-semibold transition-colors border ${
            hashtagsOn
              ? "bg-sky-100 text-sky-800 border-sky-300 hover:bg-sky-200"
              : "bg-white text-[#666] border-[#E5E5E5] hover:bg-[#F5F5F5]"
          }`}
        >
          <Hash size={10} />
          Hashtags {hashtagsOn ? "an" : "aus"}
        </button>
      </div>
      {upgradeHint && (
        <div data-testid={`ai-assist-upgrade${testIdSuffix ? "-" + testIdSuffix : ""}`}
          className="rounded-xl border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900 flex items-center gap-2">
          <span className="flex-1">
            {upgradeHint.code === "QUOTA_EXCEEDED"
              ? `Dein Kontingent ist aufgebraucht (${upgradeHint.used}/${upgradeHint.limit}). Mit einem höheren Tarif kannst du weiter generieren.`
              : `Diese Funktion ist im Tarif ${upgradeHint.plan} nicht enthalten.`}
          </span>
          <button data-testid={`ai-assist-upgrade-cta${testIdSuffix ? "-" + testIdSuffix : ""}`}
            onClick={() => navigate(upgradeHint.url)}
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-[#0A0A0A] text-white text-[10px] font-bold hover:bg-[#222]">
            {upgradeHint.cta} <ArrowUpRight size={10} />
          </button>
        </div>
      )}
      {error && <p data-testid={`ai-assist-error${testIdSuffix ? "-" + testIdSuffix : ""}`} className="text-[10px] text-red-600">{error}</p>}

      {/* iter171 · EU-AI-Act-Freigabe-Dialog */}
      <AIReviewDialog
        open={!!review}
        originalText={value}
        generated={review?.text || ""}
        action={review?.action || ""}
        onAccept={(text) => {
          onApply && onApply(text);
          setReview(null);
        }}
        onReject={() => setReview(null)}
      />
    </div>
  );
}
